/** Every way the console writes a time, in one file.
 *
 *  The old pages each kept their own (`toLocaleString()` in en-US form,
 *  `toLocaleString('ko-KR')`, `relativeTime` "2h ago", `fmtAgo` "3 min ago");
 *  new screens use these instead. Rules: 24-hour clock, month and day as
 *  "10. 17.", the year only when it is not this year, and a time typed in is
 *  always read local → UTC ISO (`fromLocalInput`).
 *
 *  Units differ by source: the API's RFC3339 strings, Play slots in unix
 *  seconds, analytics `last_event_at` in unix milliseconds and `last_pull_at`
 *  in seconds. `fromUnix`/`fromUnixMs` turn the numbers into ISO so every
 *  formatter below (and `<When>`) takes one form. */

const pad = (n: number) => String(n).padStart(2, '0')
const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토']
const DAY_MS = 86_400_000

/** A Date from an ISO string, or null for empty and unparseable input. */
export function parseTime(iso?: string | null): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function midnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** Whole calendar days from `now` to `d` in the browser's zone: 0 today,
 *  1 tomorrow, -1 yesterday. */
export function dayOffset(d: Date, now = new Date()): number {
  return Math.round((midnight(d) - midnight(now)) / DAY_MS)
}

function hm(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function md(d: Date, now: Date): string {
  const base = `${d.getMonth() + 1}. ${d.getDate()}.`
  return d.getFullYear() === now.getFullYear() ? base : `${d.getFullYear()}. ${base}`
}

/** "10. 17. 21:00" — the year only when it is another year. */
export function formatAbsolute(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return '—'
  return `${md(d, now)} ${hm(d)}`
}

/** A calendar day ("2026-10-17", or the date part of a longer string) as
 *  "10. 17." — the year only when it is another year; `withYear: false` drops
 *  it always (chart ticks). Read from the text, not through Date: a snapshot
 *  date is already the day it was taken, and `new Date('2026-10-17')` is UTC
 *  midnight, the previous day west of Greenwich. Anything else is shown as is. */
export function formatDay(date?: string | null, now = new Date(), withYear = true): string {
  if (!date) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  if (!m) return date
  const base = `${Number(m[2])}. ${Number(m[3])}.`
  return withYear && Number(m[1]) !== now.getFullYear() ? `${m[1]}. ${base}` : base
}

/** "2026. 10. 17. (금) 21:00:05" — for tooltips. */
export function formatFull(iso?: string | null): string {
  const d = parseTime(iso)
  if (!d) return ''
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}. (${WEEKDAY[d.getDay()]}) ${hm(d)}:${pad(d.getSeconds())}`
}

/** "오늘 13:35", "어제 13:35", "내일 09:00", else "10. 17. 13:35". The desk
 *  turns over daily, so this is the form tables use. */
export function formatShort(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return '—'
  const off = dayOffset(d, now)
  if (off === 0) return `오늘 ${hm(d)}`
  if (off === -1) return `어제 ${hm(d)}`
  if (off === 1) return `내일 ${hm(d)}`
  return `${md(d, now)} ${hm(d)}`
}

/** "방금", "12분 뒤", "3시간 전", "2일 뒤". */
export function formatRelative(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return '—'
  const mins = Math.round((d.getTime() - now.getTime()) / 60_000)
  const abs = Math.abs(mins)
  const side = mins >= 0 ? '뒤' : '전'
  if (abs < 1) return '방금'
  if (abs < 60) return `${abs}분 ${side}`
  if (abs < 60 * 24) return `${Math.round(abs / 60)}시간 ${side}`
  return `${Math.round(abs / (60 * 24))}일 ${side}`
}

/** "10. 17. 21:00 (2시간 뒤)" — a slot with how far off it is. */
export function formatSlot(iso?: string | null, now = new Date()): string {
  const d = parseTime(iso)
  if (!d) return ''
  return `${formatAbsolute(iso, now)} (${formatRelative(iso, now)})`
}

/** Unix seconds (Play slots, `last_pull_at`) as an ISO string; 0 and empty
 *  are "never" and give null. */
export function fromUnix(sec?: number | null): string | null {
  if (!sec || !Number.isFinite(sec) || sec <= 0) return null
  return new Date(sec * 1000).toISOString()
}

/** Unix milliseconds (analytics `last_event_at`) as an ISO string. */
export function fromUnixMs(ms?: number | null): string | null {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return null
  return new Date(ms).toISOString()
}

/** A unix-seconds timestamp in full: "2026. 10. 17. (토) 21:00:05". */
export function formatUnix(unix?: number | null): string {
  const iso = fromUnix(unix)
  return iso ? formatFull(iso) : '-'
}

/** A unix-milliseconds timestamp in full. */
export function formatUnixMs(ms?: number | null): string {
  const iso = fromUnixMs(ms)
  return iso ? formatFull(iso) : '-'
}

/** A `datetime-local` value ("2026-10-17T21:00") in the browser's zone. */
export function toLocalInput(iso?: string | null): string {
  const d = parseTime(iso)
  if (!d) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${hm(d)}`
}

/** A `datetime-local` value read in the browser's zone, as a UTC ISO string —
 *  the only form a slot is sent to the server in. Empty or malformed → null. */
export function fromLocalInput(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local.trim())
  if (!m) return null
  const [, y, mo, d, h, mi, s] = m
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0))
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** The text the server answers with too, so the two never disagree. */
export const PAST_SLOT_MESSAGE = '지난 시각에는 예약할 수 없다'

/** A slot earlier than a minute from now is refused by the server; this is the
 *  same test, so the form says so before it is sent. Null when it is fine. */
export function pastSlotError(iso: string | null | undefined, now = new Date()): string | null {
  const d = parseTime(iso)
  if (!d) return null
  return d.getTime() < now.getTime() + 60_000 ? PAST_SLOT_MESSAGE : null
}

/** "2026-10-17" in the browser's zone — the key a calendar day is filed under. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** A day heading: "오늘 10. 17. (금)", "내일 10. 18. (토)", "10. 19. (일)". */
export function dayLabel(d: Date, now = new Date()): string {
  const base = `${md(d, now)} (${WEEKDAY[d.getDay()]})`
  const off = dayOffset(d, now)
  if (off === 0) return `오늘 ${base}`
  if (off === 1) return `내일 ${base}`
  if (off === -1) return `어제 ${base}`
  return base
}

/** Days until a "YYYY-MM-DD" date (an expiry), counted in the browser's zone:
 *  0 on the day itself, negative after it, null when there is none. */
export function daysUntil(date?: string | null, now = new Date()): number | null {
  if (!date) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return dayOffset(d, now)
}

/** "D-9", "D-day", "D+2". */
export function formatDday(days: number | null): string {
  if (days === null) return '—'
  if (days === 0) return 'D-day'
  return days > 0 ? `D-${days}` : `D+${-days}`
}

/** The browser's zone name, for "Asia/Seoul 기준" under a slot input. */
export function localZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '로컬 시간'
  } catch {
    return '로컬 시간'
  }
}

const KST_OFFSET_MS = 9 * 3_600_000

/** The Seoul wall clock for an instant, "HH:mm". KST is a fixed UTC+9 with no
 *  daylight saving, so it is counted, not looked up — and it is the reader's
 *  clock whatever zone the laptop is set to (telemetry buckets are UTC and the
 *  question is "last night or this morning, for me"). */
export function formatKstClock(iso?: string | null): string {
  const d = parseTime(iso)
  if (!d) return ''
  const k = new Date(d.getTime() + KST_OFFSET_MS)
  return `${pad(k.getUTCHours())}:${pad(k.getUTCMinutes())}`
}

/** The UTC hour an instant falls in, "09-02 14:00" (month-day, no year). */
export function formatUtcHour(iso?: string | null): string {
  const d = parseTime(iso)
  if (!d) return ''
  return `${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:00`
}

/** The hour-of-day (UTC) of an instant, "14". */
export function utcHourOf(iso?: string | null): string {
  const d = parseTime(iso)
  return d ? pad(d.getUTCHours()) : ''
}
